import React from 'react';
import { 
  Users, 
  Calendar,
  Award,
  BarChart3,
  Target,
  Rocket
} from 'lucide-react';

const CustomerEngagementReportSummary = () => {
  return (
    <div className="status-report">
      <div className="report-header">
        <h1 className="report-title">
          <Users className="w-8 h-8 text-blue-600" />
          Customer Engagement Summary
        </h1>
        <div className="report-meta">
          <span className="meta-item">
            <Calendar className="w-4 h-4" />
            Oct 2 – Nov 24, 2025
          </span>
          <span className="meta-item">
            <Target className="w-4 h-4" />
            Executive Highlights
          </span>
        </div>
      </div>

      <div className="report-grid">
        {/* Overview Highlights */}
        <div className="report-section">
          <h2 className="section-title">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            Key Metrics
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="metrics-grid" style={{ marginTop: '0' }}>
                <div className="metric">
                  <span className="metric-value">9</span>
                  <span className="metric-label">Total Users</span>
                </div>
                <div className="metric">
                  <span className="metric-value">56%</span>
                  <span className="metric-label">Weekly Active Rate</span>
                </div>
                <div className="metric">
                  <span className="metric-value">5</span>
                  <span className="metric-label">Active Users</span>
                </div>
                <div className="metric">
                  <span className="metric-value">20</span>
                  <span className="metric-label">PDF Reports Generated</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Platform Improvements Highlights */}
        <div className="report-section">
          <h2 className="section-title">
            <Rocket className="w-6 h-6 text-purple-600" />
            Platform Improvements (Past 1.5 Months)
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Data Extraction & Accuracy</h3>
                <span className="status-badge completed">IMPROVED</span>
              </div>
              <ul className="achievement-list">
                <li>Fixed page number display and accuracy issues</li>
                <li>Enhanced exclusion form extraction</li>
                <li>Improved Deductible/SIR limits extraction</li>
                <li>Enhanced WC and endorsement extraction in Binders and Quotes</li>
                <li>Multiple incremental improvements to Quotes and Binders quality</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Coverage Check Enhancements</h3>
                <span className="status-badge completed">ENHANCED</span>
              </div>
              <ul className="achievement-list">
                <li>Added Additional First Name Insured field</li>
                <li>Implemented schedule matching endorsement verification</li>
                <li>Added Additional Named Insureds and Broad Form Named Insured tracking</li>
                <li>Expanded coverage standards using Baldwin Standards</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>New Features</h3>
                <span className="status-badge completed">NEW</span>
              </div>
              <ul className="achievement-list">
                <li>Added Attached/Not-Attached status in Document Comparison and Coverage Check</li>
                <li>Improved data visibility across comparison views</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Top Users Highlights */}
        <div className="report-section">
          <h2 className="section-title">
            <Award className="w-6 h-6 text-yellow-600" />
            Top Performers
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Patricia Fazzino</h3>
                <span className="status-badge completed">379 ACTIONS</span>
              </div>
              <ul className="achievement-list">
                <li>7 active days, 7 logins, 5 PDF reports</li>
                <li>Primary: Quote/Binder/Policy, Document Comparison</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Amy Klein</h3>
                <span className="status-badge completed">205 ACTIONS</span>
              </div>
              <ul className="achievement-list">
                <li>10 active days, 7 logins, 6 PDF reports</li>
                <li>Highest feature adoption across platform</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Danielle McCabe</h3>
                <span className="status-badge completed">125 ACTIONS</span>
              </div>
              <ul className="achievement-list">
                <li>5 active days, 4 logins, 2 PDF reports</li>
                <li>Only user utilizing Coverage Check feature</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Key Highlights */}
        <div className="report-section">
          <h2 className="section-title">
            <Target className="w-6 h-6 text-teal-600" />
            Key Highlights
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Engagement Summary</h3>
                <span className="status-badge completed">HIGHLIGHTS</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Top performers:</strong> Patricia Fazzino and Amy Klein drive majority of platform engagement</li>
                <li><strong>Re-engagement opportunity:</strong> 4 users inactive for 27+ days despite prior activity</li>
                <li><strong>Feature usage:</strong> Quote/Binder/Policy and Document Comparison are primary features</li>
                <li><strong>Coverage Check:</strong> Underutilized (only 1 user)</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>User Distribution</h3>
                <span className="status-badge completed">BREAKDOWN</span>
              </div>
              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>High Engagement (100+ Actions)</span>
                  <span style={{ color: '#3b82f6', fontWeight: '600' }}>5 users</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>Moderate Engagement (50-99 Actions)</span>
                  <span style={{ color: '#f59e0b', fontWeight: '600' }}>2 users</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>Light Engagement (Under 50 Actions)</span>
                  <span style={{ color: '#64748b', fontWeight: '600' }}>2 users</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <div className="report-footer">
        <p><strong>Prepared by:</strong> Analytics Team</p>
        <p><strong>Report Date:</strong> November 24, 2025</p>
        <p><strong>Report Type:</strong> Executive Summary</p>
      </div>
    </div>
  );
};

export default CustomerEngagementReportSummary;

